import { useState, useEffect, useRef, ReactNode } from "react";
import { ChevronDown, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Base props that all insets share
export interface BaseInsetProps {
  data: any[] | null;
  loading?: boolean;
  error?: string | null;
  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "middle-right";
  width?: string;
  height?: string;
  title?: string;
  subtitle?: string;
  isCollapsible?: boolean;
  initiallyCollapsed?: boolean;
  className?: string;
  headers?: {
    key: string;
    label: string;
  }[];
  areaNameKey?: string;
  isDraggable?: boolean;
  onPositionChange?: (position: { x: number; y: number }) => void;
  /** Optional info tooltip content displayed as a hover icon in the header */
  infoTooltip?: ReactNode;
  panelMode?: boolean;
}

// Shared state interface that children can access
export interface InsetState {
  collapsed: boolean;
  dimensions: { width: string; height: string };
  isResizing: boolean;
  isDragging: boolean;
  hasStartedDragging: boolean;
  position: { x: number; y: number };
}

// Props for the AbstractInset component
interface AbstractInsetProps extends BaseInsetProps {
  children: ReactNode | ((state: InsetState) => ReactNode);
  containerId: string;
  resizeAriaLabel?: string;
}

/**
 * AbstractInset Component
 *
 * A base component that provides common inset functionality including:
 * - Positioning
 * - Collapsible behavior
 * - Resizing functionality
 * - Dragging functionality
 * - Loading and error states
 * - Common styling
 */
const AbstractInset: React.FC<AbstractInsetProps> = ({
  data,
  loading = false,
  error = null,
  position = "bottom-right",
  width = "350px",
  height = "auto",
  title = "Features Data",
  subtitle,
  isCollapsible = true,
  initiallyCollapsed = false,
  className,
  children,
  containerId,
  resizeAriaLabel = "Resize inset",
  isDraggable = true,
  onPositionChange,
  infoTooltip,
  panelMode = false,
}) => {
  // Shared state
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const [dimensions, setDimensions] = useState({
    width: typeof width === "string" ? width : `${width}px`,
    height: typeof height === "string" ? height : `${height}px`,
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasStartedDragging, setHasStartedDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Refs for tracking resize state
  const resizeHandlersRef = useRef<{
    mouseMoveHandler: ((e: MouseEvent) => void) | null;
    mouseUpHandler: ((e: MouseEvent) => void) | null;
    touchMoveHandler: ((e: TouchEvent) => void) | null;
    touchEndHandler: ((e: TouchEvent) => void) | null;
  }>({
    mouseMoveHandler: null,
    mouseUpHandler: null,
    touchMoveHandler: null,
    touchEndHandler: null,
  });

  // Refs for tracking drag state
  const dragHandlersRef = useRef<{
    mouseMoveHandler: ((e: MouseEvent) => void) | null;
    mouseUpHandler: ((e: MouseEvent) => void) | null;
    touchMoveHandler: ((e: TouchEvent) => void) | null;
    touchEndHandler: ((e: TouchEvent) => void) | null;
  }>({
    mouseMoveHandler: null,
    mouseUpHandler: null,
    touchMoveHandler: null,
    touchEndHandler: null,
  });

  // Refs to store initial drag positions
  const dragInitialPosRef = useRef<{
    startX: number;
    startY: number;
    elementStartX: number;
    elementStartY: number;
  }>({
    startX: 0,
    startY: 0,
    elementStartX: 0,
    elementStartY: 0,
  });

  // Refs to store initial positions for resize
  const initialPosRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  }>({
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
  });

  // Initialize dimensions from props
  useEffect(() => {
    setDimensions({
      width: typeof width === "string" ? width : `${width}px`,
      height: typeof height === "string" ? height : `${height}px`,
    });
  }, [width, height]);

  // Initialize drag position from position prop
  useEffect(() => {
    if (panelMode) return;
    if (dragPosition === null && isDraggable) {
      const coords = getInitialCoordinates();
      setDragPosition(coords);
    }
  }, [position, isDraggable, panelMode]);

  // Get initial coordinates based on position prop
  const getInitialCoordinates = () => {
    switch (position) {
      case "top-left":
        return { x: 50, y: 10 };
      case "top-right":
        return { x: -20, y: 16 };
      case "middle-right":
        return { x: -16, y: 150 }; // Middle right positioning
      case "bottom-left":
        return { x: 16, y: -16 };
      case "bottom-right":
      default:
        return { x: -366, y: -16 };
    }
  };

  // Determine position styles
  const getPositionStyles = (): React.CSSProperties => {
    if (isDraggable && dragPosition) {
      const style: React.CSSProperties = {
        position: "absolute",
      };

      if (dragPosition.x >= 0) {
        style.left = `${dragPosition.x}px`;
      } else {
        style.right = `${Math.abs(dragPosition.x)}px`;
      }

      if (dragPosition.y >= 0) {
        style.top = `${dragPosition.y}px`;
      } else {
        style.bottom = `${Math.abs(dragPosition.y)}px`;
      }

      return style;
    }

    return { position: "absolute" };
  };

  // Get position class names for non-draggable mode
  const getPositionClassNames = () => {
    if (isDraggable && dragPosition) {
      return "";
    }

    switch (position) {
      case "top-left":
        return "top-4 left-4";
      case "top-right":
        return "top-4 right-4";
      case "middle-right":
        return "top-1/3 right-4";
      case "bottom-left":
        return "bottom-4 left-4";
      case "bottom-right":
      default:
        return "bottom-4 right-4";
    }
  };

  // Toggle collapsed state
  const toggleCollapsed = () => {
    if (isCollapsible) {
      setCollapsed((prev) => !prev);
    }
  };

  // Handle resize move (mouse)
  const createMouseMoveHandler = () => {
    return (e: MouseEvent) => {
      e.preventDefault();
      const dx = e.clientX - initialPosRef.current.startX;
      const dy = e.clientY - initialPosRef.current.startY;

      const newWidth = Math.max(200, initialPosRef.current.startWidth + dx);
      const newHeight = Math.max(150, initialPosRef.current.startHeight + dy);

      setDimensions({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      });
    };
  };

  // Handle resize move (touch)
  const createTouchMoveHandler = () => {
    return (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 0) return;

      const dx = e.touches[0].clientX - initialPosRef.current.startX;
      const dy = e.touches[0].clientY - initialPosRef.current.startY;

      const newWidth = Math.max(200, initialPosRef.current.startWidth + dx);
      const newHeight = Math.max(150, initialPosRef.current.startHeight + dy);

      setDimensions({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      });
    };
  };

  // Handle resize end
  const createEndHandler = () => {
    return () => {
      setIsResizing(false);

      if (resizeHandlersRef.current.mouseMoveHandler) {
        document.removeEventListener(
          "mousemove",
          resizeHandlersRef.current.mouseMoveHandler,
        );
        resizeHandlersRef.current.mouseMoveHandler = null;
      }

      if (resizeHandlersRef.current.mouseUpHandler) {
        document.removeEventListener(
          "mouseup",
          resizeHandlersRef.current.mouseUpHandler,
        );
        resizeHandlersRef.current.mouseUpHandler = null;
      }

      if (resizeHandlersRef.current.touchMoveHandler) {
        document.removeEventListener(
          "touchmove",
          resizeHandlersRef.current.touchMoveHandler,
        );
        resizeHandlersRef.current.touchMoveHandler = null;
      }

      if (resizeHandlersRef.current.touchEndHandler) {
        document.removeEventListener(
          "touchend",
          resizeHandlersRef.current.touchEndHandler,
        );
        resizeHandlersRef.current.touchEndHandler = null;
      }

      document.body.style.cursor = "";
    };
  };

  // Handle drag move (mouse)
  const createDragMouseMoveHandler = () => {
    return (e: MouseEvent) => {
      e.preventDefault();

      if (!hasStartedDragging) {
        setHasStartedDragging(true);
      }

      const dx = e.clientX - dragInitialPosRef.current.startX;
      const dy = e.clientY - dragInitialPosRef.current.startY;

      const container = document.getElementById(containerId)?.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const element = document.getElementById(containerId);
      if (!element) return;

      const elementRect = element.getBoundingClientRect();

      let newX = dragInitialPosRef.current.elementStartX + dx;
      let newY = dragInitialPosRef.current.elementStartY + dy;

      // Boundary checking
      const minX = 0;
      const minY = 0;
      const maxX = containerRect.width - elementRect.width;
      const maxY = containerRect.height - elementRect.height;

      newX = Math.max(minX, Math.min(maxX, newX));
      newY = Math.max(minY, Math.min(maxY, newY));

      const newPosition = { x: newX, y: newY };
      setDragPosition(newPosition);

      if (onPositionChange) {
        onPositionChange(newPosition);
      }
    };
  };

  // Handle drag move (touch)
  const createDragTouchMoveHandler = () => {
    return (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 0) return;

      if (!hasStartedDragging) {
        setHasStartedDragging(true);
      }

      const dx = e.touches[0].clientX - dragInitialPosRef.current.startX;
      const dy = e.touches[0].clientY - dragInitialPosRef.current.startY;

      const container = document.getElementById(containerId)?.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const element = document.getElementById(containerId);
      if (!element) return;

      const elementRect = element.getBoundingClientRect();

      let newX = dragInitialPosRef.current.elementStartX + dx;
      let newY = dragInitialPosRef.current.elementStartY + dy;

      // Boundary checking
      const minX = 0;
      const minY = 0;
      const maxX = containerRect.width - elementRect.width;
      const maxY = containerRect.height - elementRect.height;

      newX = Math.max(minX, Math.min(maxX, newX));
      newY = Math.max(minY, Math.min(maxY, newY));

      const newPosition = { x: newX, y: newY };
      setDragPosition(newPosition);

      if (onPositionChange) {
        onPositionChange(newPosition);
      }
    };
  };

  // Handle drag end
  const createDragEndHandler = () => {
    return () => {
      setIsDragging(false);

      setTimeout(() => {
        setHasStartedDragging(false);
      }, 10);

      if (dragHandlersRef.current.mouseMoveHandler) {
        document.removeEventListener(
          "mousemove",
          dragHandlersRef.current.mouseMoveHandler,
        );
        dragHandlersRef.current.mouseMoveHandler = null;
      }

      if (dragHandlersRef.current.mouseUpHandler) {
        document.removeEventListener(
          "mouseup",
          dragHandlersRef.current.mouseUpHandler,
        );
        dragHandlersRef.current.mouseUpHandler = null;
      }

      if (dragHandlersRef.current.touchMoveHandler) {
        document.removeEventListener(
          "touchmove",
          dragHandlersRef.current.touchMoveHandler,
        );
        dragHandlersRef.current.touchMoveHandler = null;
      }

      if (dragHandlersRef.current.touchEndHandler) {
        document.removeEventListener(
          "touchend",
          dragHandlersRef.current.touchEndHandler,
        );
        dragHandlersRef.current.touchEndHandler = null;
      }

      document.body.style.cursor = "";
    };
  };

  // Handle drag start
  const handleDragStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!isDraggable) return;

    e.preventDefault();
    e.stopPropagation();

    document.body.style.cursor = "grabbing";

    const element = document.getElementById(containerId);
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const container = element.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();

    const currentX = rect.left - containerRect.left;
    const currentY = rect.top - containerRect.top;

    if ("touches" in e) {
      dragInitialPosRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        elementStartX: currentX,
        elementStartY: currentY,
      };
    } else {
      dragInitialPosRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        elementStartX: currentX,
        elementStartY: currentY,
      };
    }

    setIsDragging(true);
    setHasStartedDragging(false);

    const mouseMoveHandler = createDragMouseMoveHandler();
    const touchMoveHandler = createDragTouchMoveHandler();
    const endHandler = createDragEndHandler();

    dragHandlersRef.current = {
      mouseMoveHandler,
      mouseUpHandler: endHandler,
      touchMoveHandler,
      touchEndHandler: endHandler,
    };

    if ("touches" in e) {
      document.addEventListener("touchmove", touchMoveHandler, {
        passive: false,
      });
      document.addEventListener("touchend", endHandler);
    } else {
      document.addEventListener("mousemove", mouseMoveHandler);
      document.addEventListener("mouseup", endHandler);
    }
  };

  // Handle resize start
  const handleResizeStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    document.body.style.cursor = "nwse-resize";

    const element = document.getElementById(containerId);
    if (!element) return;

    const rect = element.getBoundingClientRect();

    if ("touches" in e) {
      initialPosRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startWidth: rect.width,
        startHeight: rect.height,
      };
    } else {
      initialPosRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startWidth: rect.width,
        startHeight: rect.height,
      };
    }

    setIsResizing(true);

    const mouseMoveHandler = createMouseMoveHandler();
    const touchMoveHandler = createTouchMoveHandler();
    const endHandler = createEndHandler();

    resizeHandlersRef.current = {
      mouseMoveHandler,
      mouseUpHandler: endHandler,
      touchMoveHandler,
      touchEndHandler: endHandler,
    };

    if ("touches" in e) {
      document.addEventListener("touchmove", touchMoveHandler, {
        passive: false,
      });
      document.addEventListener("touchend", endHandler);
    } else {
      document.addEventListener("mousemove", mouseMoveHandler);
      document.addEventListener("mouseup", endHandler);
    }
  };

  // Clean up event listeners on unmount
  useEffect(() => {
    return () => {
      // Cleanup resize handlers
      if (resizeHandlersRef.current.mouseMoveHandler) {
        document.removeEventListener(
          "mousemove",
          resizeHandlersRef.current.mouseMoveHandler,
        );
      }
      if (resizeHandlersRef.current.mouseUpHandler) {
        document.removeEventListener(
          "mouseup",
          resizeHandlersRef.current.mouseUpHandler,
        );
      }
      if (resizeHandlersRef.current.touchMoveHandler) {
        document.removeEventListener(
          "touchmove",
          resizeHandlersRef.current.touchMoveHandler,
        );
      }
      if (resizeHandlersRef.current.touchEndHandler) {
        document.removeEventListener(
          "touchend",
          resizeHandlersRef.current.touchEndHandler,
        );
      }

      // Cleanup drag handlers
      if (dragHandlersRef.current.mouseMoveHandler) {
        document.removeEventListener(
          "mousemove",
          dragHandlersRef.current.mouseMoveHandler,
        );
      }
      if (dragHandlersRef.current.mouseUpHandler) {
        document.removeEventListener(
          "mouseup",
          dragHandlersRef.current.mouseUpHandler,
        );
      }
      if (dragHandlersRef.current.touchMoveHandler) {
        document.removeEventListener(
          "touchmove",
          dragHandlersRef.current.touchMoveHandler,
        );
      }
      if (dragHandlersRef.current.touchEndHandler) {
        document.removeEventListener(
          "touchend",
          dragHandlersRef.current.touchEndHandler,
        );
      }
    };
  }, []);

  // Create the inset state object for children
  const insetState: InsetState = {
    collapsed,
    dimensions,
    isResizing,
    isDragging,
    hasStartedDragging,
    position: dragPosition || { x: 0, y: 0 },
  };

  const headerClassNamePanel = `bg-primary px-3 py-2 flex justify-between items-center flex-shrink-0 text-primary-foreground rounded-t-xl ${
    isCollapsible ? "cursor-pointer" : "cursor-default"
  }`;

  const headerClassNameFloating = `bg-primary px-3 py-2 flex justify-between items-center flex-shrink-0 text-primary-foreground ${
    isDraggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
  }`;

  const headerNode = (
    <div
      className={panelMode ? headerClassNamePanel : headerClassNameFloating}
      onClick={
        panelMode
          ? isCollapsible && !hasStartedDragging
            ? toggleCollapsed
            : undefined
          : !hasStartedDragging
            ? toggleCollapsed
            : undefined
      }
      onMouseDown={!panelMode && isDraggable ? handleDragStart : undefined}
      onTouchStart={!panelMode && isDraggable ? handleDragStart : undefined}
      style={{
        userSelect: "none",
        touchAction: !panelMode && isDraggable ? "none" : "auto",
      }}
      title={!panelMode && isDraggable ? "Drag to move" : undefined}
    >
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-medium truncate">{title}</h3>
          {infoTooltip && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="flex-shrink-0 p-0.5 rounded hover:bg-primary-foreground/10 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <Info className="h-3.5 w-3.5 text-primary-foreground/70 hover:text-primary-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-xs z-40 bg-popover text-popover-foreground border border-border shadow-md"
                  sideOffset={8}
                >
                  {infoTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {subtitle && (
          <span className="text-xs text-primary-foreground/70">{subtitle}</span>
        )}
      </div>
      {isCollapsible && (
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`}
        />
      )}
    </div>
  );

  if (panelMode) {
    return (
      <div
        id={containerId}
        className={`flex h-full w-full min-h-0 flex-col overflow-hidden ${className ?? ""}`}
      >
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-background/95 backdrop-blur-sm rounded-xl">
          {headerNode}
          <div
            className={`min-h-0 flex-1 flex flex-col overflow-hidden transform-gpu ${collapsed ? "hidden" : ""}`}
          >
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {typeof children === "function" ? children(insetState) : children}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={containerId}
      className={`${className || `absolute ${getPositionClassNames()} z-10`} ${isResizing || isDragging ? "select-none" : ""}`}
      style={{
        width: dimensions.width,
        maxWidth: "calc(100vw - 2rem)",
        display: "flex",
        flexDirection: "column",
        cursor: isResizing ? "nwse-resize" : isDragging ? "grabbing" : "auto",
        ...getPositionStyles(),
      }}
    >
      <div
        className="bg-background/95 backdrop-blur-sm rounded-md shadow-lg border overflow-hidden flex flex-col relative"
        style={{
          height: "auto",
          maxHeight: collapsed
            ? "auto"
            : dimensions.height !== "auto"
              ? dimensions.height
              : "min(70vh, calc(100dvh - 8rem))",
          transition: isResizing ? "none" : "max-height 0.3s ease-in-out",
        }}
      >
        {headerNode}

        {/* Content */}
        <div
          style={{
            height: collapsed ? "0" : "auto",
            maxHeight: collapsed
              ? "0"
              : dimensions.height !== "auto"
                ? dimensions.height
                : "min(70vh, calc(100dvh - 8rem))",
            opacity: collapsed ? 0 : 1,
            overflow: collapsed ? "hidden" : "auto",
            transition: isResizing
              ? "none"
              : "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
            display: "flex",
            flexDirection: "column",
          }}
          className="transform-gpu"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              flex: "1 1 auto",
              minHeight: 0,
            }}
          >
            {typeof children === "function" ? children(insetState) : children}
          </div>
        </div>
      </div>

      {/* Resize handle (only visible when not collapsed) */}
      {!collapsed && (
        <div
          className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize z-10 bg-gradient-to-br from-transparent from-50% to-muted-foreground/40 to-50%"
          style={{
            borderBottomRightRadius: "4px",
            touchAction: "none",
          }}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          title="Drag to resize"
          aria-label={resizeAriaLabel}
        ></div>
      )}
    </div>
  );
};

export default AbstractInset;
export type { AbstractInsetProps };
